import React from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDate, BOOKING_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

export default function CalendarPage() {
  const [current, setCurrent] = React.useState(new Date());
  const [bookings, setBookings] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      const { data } = await api.get("/bookings");
      setBookings(data);
    })();
  }, []);

  const monthLabel = current.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const first = startOfMonth(current);
  const last = endOfMonth(current);
  const gridStart = new Date(first);
  const dow = (first.getDay() + 6) % 7; // Monday=0
  gridStart.setDate(first.getDate() - dow);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  const byDay = (day) => {
    const iso = day.toISOString().slice(0, 10);
    return bookings.filter((b) => b.start_date.slice(0, 10) <= iso && b.end_date.slice(0, 10) >= iso);
  };

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Kalender Booking</h1>
          <p className="text-sm text-neutral-500">Tampilan bulanan booking equipment.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-sm font-semibold capitalize min-w-[140px] text-center">{monthLabel}</div>
          <Button variant="outline" size="sm" onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCurrent(new Date())} data-testid="cal-today">Hari Ini</Button>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <div className="grid grid-cols-7 border-b border-neutral-200">
          {DAYS.map((d) => (
            <div key={d} className="text-xs uppercase tracking-wider font-semibold text-neutral-500 py-2 px-2 border-r border-neutral-200 last:border-r-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 auto-rows-fr min-h-[500px]">
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === current.getMonth();
            const today = d.toDateString() === new Date().toDateString();
            const items = byDay(d);
            return (
              <div key={i} className={cn(
                "border-r border-b border-neutral-200 last-in-row:border-r-0 p-1.5 min-h-[80px]",
                !inMonth && "bg-neutral-50 text-neutral-400",
                today && "bg-green-50",
              )}>
                <div className="text-xs font-semibold">{d.getDate()}</div>
                <div className="mt-1 space-y-1">
                  {items.slice(0, 3).map((b) => (
                    <Link key={b.id} to={`/bookings/${b.id}`}
                      className="block text-[10px] leading-tight px-1.5 py-0.5 rounded truncate bg-green-600 text-white hover:bg-green-700"
                      data-testid={`cal-booking-${b.id}`}>
                      {b.number} · {b.customer_name}
                    </Link>
                  ))}
                  {items.length > 3 && <div className="text-[10px] text-neutral-500">+{items.length - 3} lainnya</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card-flat p-4">
        <div className="text-sm font-semibold mb-2">Daftar Booking Aktif</div>
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th className="text-left">No.</th>
                <th className="text-left">Pelanggan</th>
                <th className="text-left">Periode</th>
                <th className="text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.filter((b) => ["confirmed", "preparing", "ready", "checked_out"].includes(b.status)).map((b) => (
                <tr key={b.id}>
                  <td className="font-mono text-xs"><Link to={`/bookings/${b.id}`} className="text-blue-700 hover:underline">{b.number}</Link></td>
                  <td>{b.customer_name}</td>
                  <td className="text-xs">{formatDate(b.start_date)} – {formatDate(b.end_date)}</td>
                  <td><StatusBadge statusMap={BOOKING_STATUS} value={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
